import { Blast, Hole, PositionXYZ } from 'src/app/services/hole.service';

// ─────────────────────────────────────────────────────────────────────────────
// Per-hole derived values (head / toe deviation, toe level).
// Ported from the legacy BlastDesignComponent so the Holes tab shares one
// implementation with the quality view.
// ─────────────────────────────────────────────────────────────────────────────

export type ToeLevelData = NonNullable<Hole['calculatedValues']>['toeLevelData'];

/** Returns a copy of the blast whose design holes carry `calculatedValues`. */
export function withCalculatedValues(blast: Blast): Blast {
  if (!blast?.BlastDesign) return blast;
  return {
    ...blast,
    BlastDesign: blast.BlastDesign.map(hole => ({
      ...hole,
      calculatedValues: {
        headPositionMagnitude: headPositionMagnitude(hole),
        toePositionMagnitude:  toePositionMagnitude(hole),
        toeLevelData:          toeLevelData(hole),
      },
    })),
  };
}

/** Horizontal distance (cm) between measured and planned collar. */
export function headPositionMagnitude(hole: Hole): number | null {
  if (!hole.PositionMeasuredXYZ || !hole.PositionPlannedXYZ) return null;
  const deltaX = (hole.PositionMeasuredXYZ.x - hole.PositionPlannedXYZ.x) * 100;
  const deltaY = (hole.PositionMeasuredXYZ.y - hole.PositionPlannedXYZ.y) * 100;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}

/**
 * Depth to use for the measured hole: the final depth when entered (`Measure.length`, 0 when not),
 * else the measured (drilled) depth. Null when neither is known.
 */
export function measuredDepth(hole: Hole): number | null {
  for (const candidate of [hole.Geometry?.Measure?.length, hole.Geometry?.drilllength]) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) return candidate;
  }
  return null;
}

/** 3D distance (cm) between measured and planned toe. */
export function toePositionMagnitude(hole: Hole): number | null {
  const depth = measuredDepth(hole);
  if (!hole.PositionPlannedXYZ || !hole.Geometry?.Design || !hole.Geometry?.Measure || depth == null) return null;

  const planned  = toePosition(hole.PositionPlannedXYZ, hole.Geometry.Design.length, hole.Geometry.Design.angle, hole.Geometry.Design.azimuth);
  const measured = toePosition(hole.PositionMeasuredXYZ ?? hole.PositionPlannedXYZ, depth, hole.Geometry.Measure.angle, hole.Geometry.Measure.azimuth);

  const deltaX = (measured.x - planned.x) * 100;
  const deltaY = (measured.y - planned.y) * 100;
  const deltaZ = (measured.z - planned.z) * 100;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ);
}

export function toeLevelData(hole: Hole): ToeLevelData {
  const planned = hole.PositionPlannedXYZ && hole.Geometry?.Design
    ? toePosition(hole.PositionPlannedXYZ, hole.Geometry.Design.length, hole.Geometry.Design.angle, hole.Geometry.Design.azimuth).z
    : null;

  const depth = measuredDepth(hole);
  const measured = hole.Geometry?.Measure && depth != null && (hole.PositionMeasuredXYZ ?? hole.PositionPlannedXYZ)
    ? toePosition(hole.PositionMeasuredXYZ ?? hole.PositionPlannedXYZ, depth, hole.Geometry.Measure.angle, hole.Geometry.Measure.azimuth).z
    : null;

  if (planned == null && measured == null) return { planned: null, measured: null, difference: null };

  // No difference without both sides (the legacy code returned 0, which hid the missing data)
  return {
    planned,
    measured,
    difference: planned == null || measured == null ? null : measured - planned,
  };
}

/**
 * Toe from collar, length and angles. Domain convention (BO `Hole`, 3D viewer): radians, angle
 * measured from the vertical (0 = pointing down), azimuth from north clockwise. The legacy design
 * tab used `sin` for the depth as well, which left a vertical hole with its toe at collar level.
 */
function toePosition(head: PositionXYZ, length: number, angle: number, azimuth: number): PositionXYZ {
  const horizontal = length * Math.sin(angle);
  return {
    x: head.x + horizontal * Math.sin(azimuth),
    y: head.y + horizontal * Math.cos(azimuth),
    z: head.z - length * Math.cos(angle),
  };
}
